import client from "@/api/client"
import { advertisementApiPath } from "@/api/advertisement-path"
import {
  campaignMetricsResponseSchema,
  campaignSummaryResponseSchema,
  campaignsResponseSchema,
} from "@/api/schemas/adsCampaign"
import { baseResponseSchema } from "@/api/schemas/shared"

export const getAdsCampaign = async (params: {
  page: number
  pageSize: number
  business?: string
  id?: string
  status?: string
  pendingApproval?: boolean
  rejected?: boolean
  active?: boolean
  search?: string
}) => {
  const response = await client.get(advertisementApiPath("/campaign"), {
    params,
  })
  return campaignsResponseSchema.parse(response.data)
}

export const getCampaignSummary = async () => {
  const response = await client.get(advertisementApiPath("/campaign/summary"))
  return campaignSummaryResponseSchema.parse(response.data)
}

export const getAdsPlan = async (params: { page: number; pageSize: number }) => {
  const response = await client.get(advertisementApiPath("/plan"), { params })
  return campaignsResponseSchema.parse(response.data)
}

export const getAdvertisementCampaignMetrics = async (params: { id: string }) => {
  const response = await client.get(
    advertisementApiPath("/campaignMetrics"),
    {
      params: { advertisementCampaign: params.id },
    }
  )
  return campaignMetricsResponseSchema.parse(response.data)
}

export const getCampaignAudit = async (id: string) => {
  const response = await client.get(advertisementApiPath(`/campaign/${id}/audit`))
  return baseResponseSchema.parse(response.data)
}

export const createAdCampaign = async (payload: unknown) => {
  const response = await client.post(advertisementApiPath("/campaign"), payload)
  return baseResponseSchema.parse(response.data)
}

export const updateAdCampaign = async (id: string, payload: unknown) => {
  const response = await client.patch(advertisementApiPath(`/campaign/${id}`), payload)
  return baseResponseSchema.parse(response.data)
}

export const approveAdCampaign = async (payload: {
  advertisementCampaign: string
  approved: boolean
  rejectionReason?: string
}) => {
  const response = await client.post(advertisementApiPath("/campaign/approve"), payload)
  return baseResponseSchema.parse(response.data)
}

export const cancelAdCampaign = async (id: string, payload?: { reason?: string }) => {
  const response = await client.post(advertisementApiPath(`/campaign/${id}/cancel`), payload ?? {})
  return baseResponseSchema.parse(response.data)
}

export const pauseAdCampaign = async (id: string) => {
  const response = await client.patch(advertisementApiPath(`/campaign/${id}/pause`))
  return baseResponseSchema.parse(response.data)
}

export const resumeAdCampaign = async (id: string) => {
  const response = await client.patch(advertisementApiPath(`/campaign/${id}/resume`))
  return baseResponseSchema.parse(response.data)
}

export const payAdCampaign = async (payload: unknown) => {
  const response = await client.post(
    advertisementApiPath("/campaign/pay"),
    payload
  )
  return baseResponseSchema.parse(response.data)
}

export const confirmAdCampaignPayment = async (reference: string) => {
  const response = await client.post("/api/v1/payment/confirmPayment", {
    reference,
  })
  return baseResponseSchema.parse(response.data)
}

export const changeAdCampaignImage = async (payload: unknown) => {
  const response = await client.post(
    advertisementApiPath("/campaign-image"),
    payload
  )
  return baseResponseSchema.parse(response.data)
}

export type AiCreditPool = "image" | "motion"

export const getAiCreditBalance = async (business: string) => {
  const response = await client.get(advertisementApiPath("/ai-credits/balance"), {
    params: { business },
  })
  return response.data as {
    message: string
    data: {
      imageBalance: number
      motionBalance: number
      imagePricePerCreditGhs: number
      motionPricePerCreditGhs: number
    }
  }
}

export const purchaseAiCredits = async (payload: {
  business: string
  pool: AiCreditPool
  quantity: number
}) => {
  const response = await client.post(
    advertisementApiPath("/ai-credits/purchase"),
    payload
  )
  return response.data as {
    message: string
    data: {
      url?: string
      accessCode?: string
      reference?: string
      paystackReference?: string
      pool: AiCreditPool
      quantity: number
      amountGhs: number
      pricePerCreditGhs: number
    }
  }
}

// AI generation can take minutes (Replicate image ~2m, motion ~5m).
// Override the default 30s axios timeout for these endpoints.
const AI_IMAGE_REQUEST_TIMEOUT_MS = 150_000
const AI_MOTION_REQUEST_TIMEOUT_MS = 330_000

export const generateAdCreative = async (payload: {
  business: string
  variantCount: number
  prompt?: string
  headline?: string
  style?: string
  serviceId?: string
  referenceImageUrl?: string
}) => {
  const response = await client.post(
    advertisementApiPath("/generate-creative"),
    payload,
    { timeout: AI_IMAGE_REQUEST_TIMEOUT_MS }
  )
  return response.data as {
    message: string
    data: {
      jobId: string
      variants: Array<{ url: string; index: number }>
      errors: Array<{ index: number; message: string }>
      creditsUsed: number
      creditsRefunded: number
      imageBalance: number
      motionBalance: number
    }
  }
}

export const generateAdMotionCreative = async (payload: {
  business: string
  variantCount: number
  prompt?: string
  headline?: string
  sourceImageUrl?: string
  durationSeconds?: number
}) => {
  const response = await client.post(
    advertisementApiPath("/generate-motion-creative"),
    payload,
    { timeout: AI_MOTION_REQUEST_TIMEOUT_MS }
  )
  return response.data as {
    message: string
    data: {
      jobId: string
      variants: Array<{ url: string; index: number }>
      errors: Array<{ index: number; message: string }>
      durationSeconds: number
      creditsUsed: number
      creditsRefunded: number
      imageBalance: number
      motionBalance: number
    }
  }
}
